/**
 * NanoStream Studio Interface
 *
 * These methods maintain the state - and are independent from any UI implementation
 *
 */

import MAINSTATE, {STATE_REMIX, STATE_CHROMAKEY} from "./State"
import CONFIG from "../config/Config";

let
	timer,
	notifyUpdate = null,
	returnToLoginHandler = null;

const
	TAG = "[PlaylistApi]",
	DEBUG = false,
	REFRESH_INTERVAL = 5000,
	ERROR_STREAM_NOT_FOUND_IN_MEMORY = "is new to the system";

/**
 * Sends a request to the Api Backend
 * @param {string} endpoint
 * @param {Object} data
 * @param {Object} state
 * @param {string} method
 * @param {boolean} ignoreErrors
 * @returns {Promise<any>}
 * @private
 */

async function _sendRequest(endpoint, data, state = MAINSTATE, method = "POST", ignoreErrors = false) {

	const
		apiUrl = `${CONFIG.apiserver}/${endpoint}`,
		response = await fetch(apiUrl, {
			method,
			headers: {
				...MAINSTATE.token && {nanotoken: MAINSTATE.token},
				"Content-Type": "application/json"
			},
			...method !== "GET" && data && {body: JSON.stringify(data)}
		}),

		result = await response.json();

	if (!ignoreErrors && !result.success) {

		if (result.error) {
			if (result.error.message) {
				state.error = result.error?.message;
			} else if (result.error !== "Error executing list") {
				state.error = result.error;
			}
		} else if (result.message) {
			state.error = result.message;
		}

		if (result.status === 401 && returnToLoginHandler) {
			// authentication error !
			clearPersistedAuth();
			returnToLoginHandler();
		}

	} else {
		state.error = null;
	}

	if (DEBUG) console.log(TAG, "request", apiUrl, data || null, result);
	result.requestTs = Date.now();
	return result;

}

/**
 * Load Bucket Contents
 * @param {string} service The service whose assets we want to list (watermark / remix / ....)
 * @param {string} streamname The target streamname
 * @param {string} type The type of accepted assets
 * @returns {Promise<void>}
 */

async function loadContent(STATE, streamnameOrOrga, overrideType) {

	const
		type = overrideType || STATE.type,
		streamname = STATE.perorga && streamnameOrOrga.length === 5 ? streamnameOrOrga + "-xxxxx" : streamnameOrOrga,
		list = STATE && await _sendRequest(
			"content",
			{streamname, type: type || STATE.type,service: STATE.service, perorga: STATE.perorga ? "perorga" : ""},
			STATE
		);

	STATE.content = list.data ? {ts: Date.now(), list: list.data, webroot: list.webroot} : null;

	if (STATE.content?.list) {
		let size = 0;
		STATE.content.list.forEach((i) => (size += i.size));
		STATE.content.size = size;
		sortContent(STATE.service, 0);
	}
}

function sortContent(service, delta) {

	const
		state = MAINSTATE.services[service];

	if (state.content?.list?.length) {
		state.content.sort = ((state.content.sort || 0) + (delta || 0)) % 3;
		state.content.list = state.content.list.sort(
			state.content.sort === 0
				? (a, b) => (a.key < b.key ? -1 : 1)
				: state.content.sort === 1
					? (a, b) => a.size < b.size ? 1 : -1
					: (a, b) => (new Date(a.date).valueOf() < new Date(b.date).valueOf() ? 1 : -1)
		);
	}
}

async function fetchVtransPlaylistStatus(streamname) {

	const
		valid = streamname?.length === 11,
		streamMap = valid && (STATE_REMIX.streamMap || await peekStreamName(streamname));

	if (!streamMap) {
		return null;
	}

	STATE_REMIX.streamMap = streamMap;

	const
		data = await _sendRequest(
			"list",
			{streamname},
			STATE_REMIX
		);

	if (!data?.success && data?.message) {

		if (data.message.endsWith(ERROR_STREAM_NOT_FOUND_IN_MEMORY)) {
			// Well known message: The remix is not started because tthe FQDN mapping is
			// not in server's memory
			return null;
		} else throw new Error(data.message);
	}

	return data?.data || null;
}


async function reconfigVtransOpcode(streamname, opcodeId, parameters, fqdn = "t3custom-vtrans-eu-svbm-ams5-01.vtrans-custom.nanocosmos.de") {


	if (!parameters.filter) parameters.filter = "chromakey"

	const
		{color, filter} = parameters,
		validColor = filter === "hsvkey"
			? validateHsl(color)
			: validateRgb(color);

	if (validColor && parameters.blending > 0 && parameters.blending <= 1 && parameters.similarity > 0 && parameters.similarity <= 1) {

		const
			data = await _sendRequest(
				"chromakey",
				{streamname, opcodeId, ...parameters, color: validColor, fqdn},
				STATE_CHROMAKEY
			);

		if (!data?.success && data?.message) {

			if (data.message.endsWith(ERROR_STREAM_NOT_FOUND_IN_MEMORY)) {
				// Well known message: The remix is not started because tthe FQDN mapping is
				// not in server's memory
				return null;
			} else throw new Error(data.message);
		}

		return data?.data || null;
	}
	throw new Error("incorrect parameters");
}

function startTimerForRefresh(enable) {

	clearTimeout(timer);

	if (enable) {
		timer = setTimeout(() => {
			if (!document.hidden) {
				refreshPlaylist()
					.catch((e) => {
						// error refreshing playlist
						if (DEBUG) console.error(TAG, "Error refreshing playlist", e.message);
					})
					.finally(() => {
						startTimerForRefresh(true);
					})
			} else {
				startTimerForRefresh(true);
			}
		}, REFRESH_INTERVAL);
	}
}

/**
 * Sets current session (streamname)
 * @param streamname
 * @returns {Promise<*>}
 */

async function setLivePlaylist(streamname) {

	const
		organization = streamname.split("-")[0],
		livePlaylistStatus = await fetchVtransPlaylistStatus(streamname);

	if (livePlaylistStatus) {

		const
			setlistData = await setlistGet(organization, livePlaylistStatus.setlistId, true);

		if (setlistData?.success) {

			Object.assign(STATE_REMIX, {
				streamname,
				livePlaylistStatus,
				ts: Date.now(),
				livePlaylist: setlistData.data,
				currentSetlist: setlistData.data
			});

			startTimerForRefresh(true);

			return livePlaylistStatus;

		} else {
			throw new Error(`error retrieving current setlist ${livePlaylistStatus.setlistId}`);
		}
	} else {
		if (streamname.length === 11)
			Object.assign(STATE_REMIX, {
				streamname,
				livePlaylistStatus: null,
				empty: true
			})
	}
}

/**
 * Refresh Playlist
 * @returns {Promise<void>}
 */

async function refreshPlaylist() {

	const
		{streamname} = STATE_REMIX,
		livePlaylistStatus = await fetchVtransPlaylistStatus(streamname);

	STATE_REMIX.livePlaylistStatus = livePlaylistStatus;
	STATE_REMIX.ts = Date.now();

	// remove cued video
	if (STATE_REMIX.cued === livePlaylistStatus?.current) {
		delete STATE_REMIX.cued;
	}

	// update initialIndex, if playlist is stopped it will resume from here
	if (livePlaylistStatus?.playing && livePlaylistStatus?.current >= 0)
		STATE_REMIX.initialIndex = (livePlaylistStatus?.current);

	if (STATE_REMIX.currentSetlist) {
		// remove video scheduled for deletion
		const {videos} = STATE_REMIX.currentSetlist;

		for (let i = videos.length - 1; i >= 0; i--) {
			if (videos[i].deleted) {
				videos.splice(i, 1);
			}
		}
	}
	notifyUpdate && notifyUpdate();

}

/**
 * Start a Remix Session
 * @param {Object} setlist The setlist to be made active
 * @param {string} bitrate
 * @param {number} initialIndex
 * @returns {Promise<void>}
 */

async function startRemix(setlist, bitrate = "2500k", initialIndex = 0) {

	const
		{setlistId, videos} = setlist,
		{streamname} = STATE_REMIX;


	if (!videos.length) {
		throw new Error("Please add some videos to the setlist");
	}
	if (!setlistId) {
		throw new Error("Invalid setlist, does not include setlistId");
	}
	if (!streamname) {
		throw new Error("Invalid state, there is not an active stream name");
	}

	let startData;

	try {
		STATE_REMIX.mainBusy = true;
		notifyUpdate && notifyUpdate();

		startData = await _sendRequest("start", {
			streamname,
			setlistId,
			bitrate,
			initialIndex
		}, STATE_REMIX);
	} catch (e) {
		throw e;
	} finally {
		STATE_REMIX.mainBusy = false;
		notifyUpdate && notifyUpdate();
	}
	if (startData.success) {

		Object.assign(STATE_REMIX, {
			bitrate,
			fqdn: startData.fqdn
		});

		await setLivePlaylist(streamname);

	} else {
		throw new Error(startData.error || startData.message);
	}
}

/**
 * Stops the current remix session
 * @returns {Promise<void>}
 */

async function stopRemix() {

	const {streamname} = STATE_REMIX;

	if (streamname) {
		try {
			STATE_REMIX.mainBusy = true;
			notifyUpdate && notifyUpdate();
			await _sendRequest("stop", {
				streamname
			});
			await deleteStreamName(streamname);
			await refreshPlaylist();
		} finally {
			STATE_REMIX.mainBusy = false;
			notifyUpdate && notifyUpdate();
		}
	}
}

/**
 * Plays a specific clip
 * @param {number} index
 * @returns {Promise<void>}
 */
async function playLiveIndex(index) {
	if (STATE_REMIX.livePlaylistStatus?.playing) {
		STATE_REMIX.cued = index;
		notifyUpdate && notifyUpdate();
		try {
			const playCommand = await _sendRequest("play", {
				streamname: STATE_REMIX.streamname,
				index
			}, STATE_REMIX, undefined, true);
			if (!playCommand.success) throw new Error(playCommand.message);
		} catch (e) {
			throw e;
		} finally {
			// WILL BE DELETED WHEN REFRESHING THE PLAYLIUST >>> delete STATE_REMIX.cued;
			notifyUpdate && notifyUpdate();
		}
	}
}

/**
 * Add clip
 * @param {string} streamname
 * @param {string} url
 * @param {number} index
 * @param {number} order
 * @param {boolean} loop
 * @returns {Promise<void>}
 */

async function _addLiveClip(setlist, url, index, order, loop) {

	if (STATE_REMIX.streamname && setlist?.videos) {

		const

			{setlistId, bintuOrganization, videos} = setlist,

			// First - Add Live Clip to VTRANS session
			liveVideoResult = await _sendRequest("add", {
				streamname: STATE_REMIX.streamname,
				url,
				index,
				loop: loop ? "loop" : ""
			}, STATE_REMIX);

		// If added to the Live Session - then persist into setlist
		if (liveVideoResult?.success) {

			const
				newVideoResult = await setlistAddVideo(bintuOrganization, setlistId, url, order);

			if (newVideoResult.success) {
				return newVideoResult;
			} else {
				throw new Error(newVideoResult.error || "Error adding live clip");
			}

		}

		throw new Error("Error adding live clip");
	}
}

async function insertVideo(setlist, url, index, newOrder) {

	const
		isLive = isSetlistLive(setlist.setlistId),
		{videos} = setlist,
		STATE = STATE_REMIX;

	let
		result;

	if (index === undefined) index = videos.length;
	if (newOrder === undefined) newOrder = (index + 1) * 10000;

	result = isLive
		? await _addLiveClip(
			setlist,
			url,
			index,
			newOrder,
			STATE.loop
		)
		: await setlistAddVideo(
			setlist.bintuOrganization,
			setlist.setlistId,
			url,
			newOrder
		);

	if (result.success) {

		videos.push(result.data);
		setlist.videos = videos.sort((a, b) => a.order < b.order ? -1 : 1)

	} else {
		throw new Error(result.error);
	}

	return result;
}


/**
 * Remove clip
 * @param {number} index
 * @returns {Promise<void>}
 */

async function removeLiveClip(index) {

	if (STATE_REMIX.streamname && STATE_REMIX.livePlaylist?.videos && index < STATE_REMIX.livePlaylist.videos.length) {

		const
			{videos, setlistId, bintuOrganization} = STATE_REMIX.livePlaylist,
			video = videos[index];

		// mark for deletion
		video.deleted = true;

		// update UI for deleting status
		notifyUpdate && notifyUpdate();

		try {
			// Remove video from the live setlist (VTRANS)
			await _sendRequest("remove", {streamname: STATE_REMIX.streamname, index}, STATE_REMIX);
		} catch (e) {
			video.deleted = false;
			throw e;
		}

		// TODO -> IF THIS FAILS WE HAVE A PROBLEM

		// Remove also from Persisted Video !!!
		await setlistRemoveVideo(bintuOrganization, setlistId, video.videoId);


	}
}

/**
 * Authentication: Main Login Endpoint
 * @param {string} email
 * @param {string} password
 * @returns {Promise<boolean>}
 */

async function authLogin(email, password) {

	try {
		const authData = await _sendRequest("auth", {email, password}, MAINSTATE);
		if (authData.success) {

			MAINSTATE.organization = authData.data.hash;
			MAINSTATE.token = authData.token;

			// store token in localStorage so it survives page refresh, etc.
			localStorage.nstoken = authData.data.hash + "|" + authData.token;

			return authData;

		} else {

			delete MAINSTATE.token;
			delete MAINSTATE.organization;

			delete localStorage.nstoken;

			MAINSTATE.error = authData.error;
			return false;
		}
	} catch (e) {
		delete MAINSTATE.availableServices;
		delete MAINSTATE.organization;
		delete MAINSTATE.token;

		delete localStorage.nstoken;

		MAINSTATE.error = e.message;
		return false;
	}
}

async function authLoginOrganization(orgahash) {

	if (MAINSTATE.organization) {
		if (MAINSTATE.organization !== orgahash) {
			clearPersistedAuth();
			return false;
		}
	} else {
		return false;
	}

	if (MAINSTATE.availableServices && MAINSTATE.version) {
		// cached services
		return true;
	}

	// call refresh credentials endpoint, it will return the credentials if the user
	// previously logged into that server and the token is not expired

	try {

		const authData = await _sendRequest(`auth/${orgahash}`, null, MAINSTATE);

		if (authData.success) {
			Object.assign(MAINSTATE, {
				version: authData.version,
				availableServices: authData.services
			});
			return authData;
		} else {
			MAINSTATE.error = authData.error;
		}
	} catch (e) {
		MAINSTATE.error = e.message;
	}

	delete MAINSTATE.availableServices;
	clearPersistedAuth();
	return null;

}

/**
 * Clears persisted authentication
 */

function clearPersistedAuth() {
	if (DEBUG) console.log(TAG, "- Clearing persisted credentials");
	delete localStorage.nstoken;
	delete MAINSTATE.organization;
	delete MAINSTATE.token;
}

/**
 * Authentication: Load Client-persisted credentials from localStorage. Performs
 * a cheap check on expiration. But credentials are not checked here for validity,
 * that will be performed client side on each api call
 * @return {boolean} Whether persisted credentials exist, seem valid, and have not expired
 */

function loadPersistedAuth() {

	// token is saved into localStorage to persist credentials between page loads

	if (localStorage.nstoken?.length > 128) {

		const
			[orga, token] = localStorage.nstoken.split("|");

		// there is a persisted token, early-check expiration
		if (orga && token) try {

			const expiration = JSON.parse(atob(localStorage.nstoken.substring(6).split(".")[1]))?.exp;
			if (expiration && (expiration - (Date.now() / 1000) > 0)) {

				if (DEBUG) console.log(TAG, "- Loading client persisted token for organization", orga);

				MAINSTATE.organization = orga;
				MAINSTATE.token = token;
				return true;
			}

		} catch (e) {
			// token tampered with
			if (DEBUG) console.log(TAG, "- Invalid persisted credentials");
		}
	}
	// no persisted or invalid
	clearPersistedAuth();
}

/**
 * Check if playlist contains a file
 * @param filePath
 * @param playlist
 * @returns {boolean}
 */

function contains(filePath, playlist = STATE_REMIX.livePlaylist?.videos) {
	if (playlist) for (const file of playlist.videos) if (file.url.endsWith(filePath)) return true;
}

function setNotifyUpdate(handler) {
	notifyUpdate = handler
}

function setReturnToLoginHandler(handler) {
	returnToLoginHandler = handler
}

///////////////////////////////////
// Persisted API

/**
 * Get list of setlists
 * @param {string} organization
 * @returns {Promise<any>}
 */
async function setlistList(organization) {
	return _sendRequest(`setlist/${organization}`, undefined, STATE_REMIX, "GET");
}

/**
 * Get a setlist
 * @param {string} organization
 * @param {string} setlistId
 * @param {boolean} includeVideos
 * @returns {Promise<any>}
 */

async function setlistGet(organization, setlistId, includeVideos = false) {
	return _sendRequest(`setlist/${organization}/${setlistId}${includeVideos ? "?videos=1" : ""}`, undefined, STATE_REMIX, "GET");
}

/**
 * Add a video to a setlist
 * @param {string} organization The Organization
 * @param {string} setlistId The setlist ID
 * @param {string} url The video URL
 * @param {number} order The order factor
 * @returns {Promise<any>}
 */

async function setlistAddVideo(organization, setlistId, url, order) {
	return _sendRequest(`setlist/${organization}/${setlistId}`, {url, order}, STATE_REMIX, "PATCH");
}

/**
 * Removes a video in a setlist
 * @param {string} organization
 * @param {string} setlistId
 * @param {string} videoId
 * @returns {Promise<any>}
 */
async function setlistRemoveVideo(organization, setlistId, videoId) {
	return _sendRequest(
		`setlist/${organization}/${setlistId}/${videoId}`,
		undefined,
		STATE_REMIX,
		"DELETE"
	);
}

/**
 * Removes a video in a setlist
 * @param {string} organization
 * @param {string} setlistId
 * @param {string} videoId
 * @param {number} order
 * @returns {Promise<any>}
 */
async function setlistReorderVideo(organization, setlistId, videoId, order) {
	return _sendRequest(
		`setlist/${organization}/${setlistId}/${videoId}`,
		{order},
		STATE_REMIX,
		"PATCH"
	);
}

/**
 * Deletes a setlist
 * @param {string} organization
 * @param {string} setlistId
 * @returns {Promise<any>}
 */
async function setlistRemove(organization, setlistId) {
	return _sendRequest(
		`setlist/${organization}/${setlistId}`,
		undefined,
		STATE_REMIX,
		"DELETE"
	);
}

/**
 * Creates a new setlist
 * @param {string} organization
 * @param {string} title
 * @returns {Promise<any>}
 */
async function setlistCreate(organization, title) {
	return _sendRequest(
		`setlist/${organization}`,
		{title},
		STATE_REMIX,
		"PUT"
	);
}

/**
 * Check a streamname is currently mappedf into a Vtrans server
 * @param {string} streamname
 */

async function peekStreamName(streamname) {

	try {

		const
			valid = streamname?.length === 11,
			streamMap = valid && await _sendRequest(
				`stream/${streamname}`,
				undefined,
				STATE_REMIX,
				"GET",
				true
			);

		if (DEBUG) console.log(TAG, "peekStreamName", streamMap);

		if (streamMap?.success) return streamMap.data;
		else return null;

	} catch (e) {
		if (DEBUG) console.warn(TAG, "peekStreamName", e.message);
		return null;
	}
}


async function deleteStreamName(streamname) {

	try {

		const
			valid = streamname?.length === 11,
			streamMap = valid && await _sendRequest(
				`stream/${streamname}`,
				undefined,
				STATE_REMIX,
				"DELETE"
			);

		if (DEBUG) console.log(TAG, "deleteStreamName", streamMap);

		if (streamMap?.success) {
			delete STATE_REMIX.streamMap;
			return true;
		} else return false;

	} catch (e) {
		if (DEBUG) console.warn(TAG, "peekStreamName", e.message);
		return false;
	}
}

function isSetlistLive(setlistId) {
	return STATE_REMIX.livePlaylistStatus?.setlistId === setlistId && STATE_REMIX.livePlaylistStatus?.playing;
}

function validateHsl(color) {
	return color && color.split(":").length === 3 ? color : null;
}

function validateRgb(color) {
	return color && color.length === 6 && parseInt(color, 16) > 0 ? ("0x" + color).toLowerCase() : null;
}

export {

	setNotifyUpdate,
	setReturnToLoginHandler,

	authLogin,
	authLoginOrganization,
	loadPersistedAuth,
	clearPersistedAuth,

	// load bucket contents, multi-service
	loadContent,
	sortContent,

	// remix interface
	startRemix,
	stopRemix,
	peekStreamName,
	deleteStreamName,

	// remix playlist interface
	refreshPlaylist,
	setLivePlaylist,
	removeLiveClip,
	playLiveIndex,
	contains,
	insertVideo,

	// persisted setlist interface
	setlistGet,
	setlistList,
	setlistCreate,
	setlistRemove,
	setlistAddVideo,
	setlistRemoveVideo,
	setlistReorderVideo,

	startTimerForRefresh,
	isSetlistLive,

	reconfigVtransOpcode
}
